import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { useTranslation } from "react-i18next";
import { updatePassword } from "../services/authService";
import i18n from "../i18n";

const LANGUAGE_CODES: Record<"eng" | "rus", string> = {
  eng: "en_US",
  rus: "ru_RU",
};

const LANG_OPTIONS = [
  { value: "rus", label: "Русский" },
  { value: "eng", label: "English" },
];

const Profile: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t, i18n: i18nInstance } = useTranslation();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [language, setLanguage] = useState<"rus" | "eng">("rus");

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");

  useEffect(() => {
    const rawUser = localStorage.getItem("user");
    if (rawUser) {
      try {
        const u = JSON.parse(rawUser);
        setName(u.name || u.username || "");
        setEmail(u.email || u.emailAddress || "");
      } catch (err) {
        console.error("Failed to parse user data from localStorage:", err);
      }
    }
    const currentLang =
      i18nInstance.language ||
      localStorage.getItem("language") ||
      LANGUAGE_CODES.rus;
    const isEng =
      currentLang === LANGUAGE_CODES.eng ||
      currentLang === "en_US" ||
      currentLang.startsWith("en");
    setLanguage(isEng ? "eng" : "rus");
  }, [i18nInstance.language]);

  const handleLanguageChange = async (
    event: SelectChangeEvent<"eng" | "rus">,
  ) => {
    const next = event.target.value as "eng" | "rus";
    const langCode = LANGUAGE_CODES[next];
    localStorage.setItem("language", langCode);
    setLanguage(next);
    const i18nLang = next === "eng" ? "en" : "ru";
    await i18n.changeLanguage(i18nLang);
  };

  const handleOpenPasswordDialog = () => setPasswordDialogOpen(true);
  const handleClosePasswordDialog = () => {
    setCurrentPassword("");
    setNewPassword("");
    setRepeatPassword("");
    setPasswordDialogOpen(false);
  };

  const handlePasswordChange = async () => {
    try {
      const rawUser = localStorage.getItem("user");
      if (!rawUser) throw new Error("User data not found in localStorage");
      const u = JSON.parse(rawUser);
      if (!u.id) throw new Error("User ID not found");
      if (newPassword !== repeatPassword) {
        alert(t("profile.passwordMismatch"));
        return;
      }
      await updatePassword(u.id, currentPassword, newPassword);
      handleClosePasswordDialog();
      alert(t("profile.passwordChanged"));
    } catch (err) {
      console.error("Failed to update password:", err);
      alert(t("profile.passwordChangeError"));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.removeItem("preferences");
    localStorage.removeItem("metadata");
    localStorage.removeItem("language");
    localStorage.removeItem("tasks");
    localStorage.removeItem("meetings");
    localStorage.removeItem("userData");
    sessionStorage.removeItem("home_filters");
    sessionStorage.removeItem("tasks_filters");
    window.location.href = "/login";
  };

  const renderButtons = () => (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        width: { xs: "100%", sm: "auto" },
        justifyContent: { xs: "center", sm: "flex-end" },
      }}
    >
      <Button
        variant="contained"
        onClick={handleOpenPasswordDialog}
        sx={{
          textTransform: "none",
          width: { xs: "50%", sm: "auto" },
          px: 3,
        }}
      >
        {t("profile.changePassword")}
      </Button>

      <Button
        variant="contained"
        color="error"
        onClick={handleLogout}
        sx={{
          textTransform: "none",
          width: { xs: "50%", sm: "auto" },
          px: 3,
        }}
      >
        {t("profile.logout")}
      </Button>
    </Stack>
  );

  return (
    <Container sx={{ px: { xs: 0, sm: 3 } }}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            mb: 3,
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
          >
            {t("profile.header")}
          </Typography>
          {!isMobile && renderButtons()}
        </Box>

        <Box
          component="form"
          noValidate
          sx={{ width: "100%", maxWidth: 600, mx: "auto" }}
        >
          <TextField
            label={t("profile.nameLabel")}
            value={name}
            fullWidth
            margin="normal"
            InputProps={{ readOnly: true }}
          />

          <TextField
            label={t("profile.emailLabel")}
            value={email}
            fullWidth
            margin="normal"
            InputProps={{ readOnly: true }}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>{t("profile.languageLabel")}</InputLabel>
            <Select
              label={t("profile.languageLabel")}
              value={language}
              onChange={handleLanguageChange}
            >
              {LANG_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {isMobile && (
          <Box sx={{ mt: "auto", px: { xs: 0, sm: 3 }, pb: 2 }}>
            {renderButtons()}
          </Box>
        )}

        <Dialog
          open={passwordDialogOpen}
          onClose={handleClosePasswordDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t("profile.changePasswordTitle")}</DialogTitle>
          <DialogContent>
            <TextField
              label={t("profile.currentPasswordLabel")}
              type="password"
              fullWidth
              margin="normal"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <TextField
              label={t("profile.newPasswordLabel")}
              type="password"
              fullWidth
              margin="normal"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <TextField
              label={t("profile.repeatPasswordLabel")}
              type="password"
              fullWidth
              margin="normal"
              value={repeatPassword}
              onChange={(e) => setRepeatPassword(e.target.value)}
              error={repeatPassword !== "" && newPassword !== repeatPassword}
              helperText={
                repeatPassword !== "" && newPassword !== repeatPassword
                  ? t("profile.passwordMismatch")
                  : undefined
              }
            />
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleClosePasswordDialog}
              sx={{ textTransform: "none" }}
            >
              {t("profile.cancelButton")}
            </Button>
            <Button
              variant="contained"
              onClick={handlePasswordChange}
              sx={{ textTransform: "none" }}
            >
              {t("profile.saveButton")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default Profile;
